import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSpring, animated, config } from '@react-spring/web';
import { useDrag } from 'react-use-gesture';

const StretchableHorizontalSlider = ({ children, pagingData, onPageChange, width, brand }) => {
    // 기본 스프링 상태
    const [{ x, springWidth }, set] = useSpring(() => ({
        x: 0,
        springWidth: 1,
        config: config.stiff
    }));

    const [stretchAmount, setStretchAmount] = useState(0);
    const containerRef = useRef(null);
    const startXRef = useRef(0);
    const dragAmountRef = useRef(0);
    const stretchDirectionRef = useRef(0);
    const [stretchDirection, setStretchDirection] = useState(0);

    // 스프링 효과를 위한 추가 상태
    const [isSpringBack, setIsSpringBack] = useState(false);

    const performImprovedSpringAnimation = useCallback(() => {
        // 스트레치 방향 저장
        stretchDirectionRef.current = stretchDirection;

        // 기존 애니메이션의 1단계 - 빠른 확장 단계를 위한 설정
        const expandConfig = {
            tension: 5000,    // 매우 높은 장력
            friction: 10,     // 매우 낮은 마찰력
            mass: 1,          // 기본 질량
            clamp: false,     // 자연스러운 움직임 허용
        };

        // 확장 정도 계산
        const expandAmount = 1.01; // 기존 값과 동일

        // 단일 애니메이션으로 진동 적용
        setIsSpringBack(true);

        // 중요: 여기서 직접적인 커스텀 애니메이션 시퀀스를 생성
        // 1. 먼저 빠르게 확장하고
        // 2. 감쇠 진동으로 1로 돌아옴
        const startTime = Date.now();
        let firstPhaseComplete = false;

        // 애니메이션 프레임 요청
        const animateFrame = () => {
            const elapsed = Date.now() - startTime;

            // 첫 번째 단계 (빠른 확장) - 50ms 동안 지속
            if (elapsed < 50 && !firstPhaseComplete) {
                // 확장 애니메이션
                set({ springWidth: expandAmount, config: expandConfig });
            }
            // 두 번째 단계 (감쇠 진동으로 복귀)
            else if (!firstPhaseComplete) {
                firstPhaseComplete = true;

                // 더 오래 지속되는 고주파 진동을 위한 설정
                const oscillationConfig = {
                    tension: 800,     // 매우 높은 장력으로 매우 빠른 진동 주기
                    friction: 5,      // 낮은 마찰력으로 진동이 더 오래 지속
                    mass: 0.2,        // 매우 가벼운 질량으로 매우 빠른 반응
                    velocity: 20,     // 매우 높은 초기 속도로 강한 초기 진동
                    precision: 0.000001 // 매우 높은 정밀도로 미세한 진동까지 계산
                };

                // 진동으로 복귀
                set({
                    springWidth: 1,
                    config: oscillationConfig,
                    onRest: () => {
                        setIsSpringBack(false);
                        stretchDirectionRef.current = 0; // 리셋
                    }
                });
            }

            // 애니메이션이 완료되지 않았다면 다음 프레임 요청
            if (!firstPhaseComplete || (firstPhaseComplete && elapsed < 1000)) {
                requestAnimationFrame(animateFrame);
            }
        };

        // 애니메이션 시작
        requestAnimationFrame(animateFrame);
    }, [set, stretchDirection]);

    const bind = useDrag(({ movement: [mx], direction: [xDir], event, last }) => {
        if (event && event.type.includes('touch')) {
            const touch = event.touches[0] || event.changedTouches[0];
            if (!startXRef.current) {
                startXRef.current = touch.clientX;
                return;
            }
            const currentX = touch.clientX;
            mx = startXRef.current - currentX;
        }

        if (!last) {
            dragAmountRef.current += mx;
            const containerWidth = containerRef.current?.offsetWidth || 0;
            const maxStretchAmount = containerWidth * 0.3;

            // 저항감 로직 
            if (!pagingData.hasPrevious && mx > 0) {
                const resistanceFactor = 0.4;
                mx *= resistanceFactor;
            } else if (!pagingData.hasNext && mx < 0) {
                const resistanceFactor = 0.4;
                mx *= resistanceFactor;
            } else {
                const resistanceFactor = 0.1;
                mx *= resistanceFactor;
            }

            const stretchFactor = Math.abs(mx) > maxStretchAmount
                ? maxStretchAmount
                : Math.abs(mx) * 0.5;

            setStretchAmount(Math.min(stretchFactor, maxStretchAmount));
            setStretchDirection(mx > 0 ? -1 : 1);
        }

        const containerWidth = containerRef.current?.offsetWidth || 0;
        const slideThreshold = containerWidth * 0.08;

        if (last) {
            startXRef.current = 0;
            setStretchAmount(0);
            setStretchDirection(0);

            // 조건부 스프링 애니메이션 
            if (Math.abs(dragAmountRef.current) > slideThreshold) {
                // 마지막 페이지에서 다음 페이지를 호출할 때
                if (dragAmountRef.current < 0 && !pagingData.hasNext) {
                    setIsSpringBack(true);
                    performImprovedSpringAnimation();
                }
                // 첫 페이지에서 이전 페이지를 호출할 때
                else if (dragAmountRef.current > 0 && !pagingData.hasPrevious) {
                    setIsSpringBack(true);
                    performImprovedSpringAnimation();
                }
                // 일반적인 페이지 변경의 경우
                else {
                    if (dragAmountRef.current > 0 && pagingData.hasPrevious) {
                        onPageChange('prev');
                    }
                    else if (dragAmountRef.current < 0 && pagingData.hasNext) {
                        onPageChange('next');
                    }
                }
            }

            dragAmountRef.current = 0;
            set({ x: 0 });
        } else {
            if (!pagingData.hasPrevious && mx > 0) {
                set({ x: mx / 3 });
                return;
            }

            if (!pagingData.hasNext && mx < 0) {
                set({ x: mx / 3 });
                return;
            }

            set({ x: mx });
        }
    }, { bounds: { left: 0, right: 0 }, rubberband: true, axis: 'x' });

    useEffect(() => {
        // 모든 애니메이션 관련 상태 초기화
        set({ x: 0, springWidth: 1 });
        setStretchAmount(0);
        setStretchDirection(0);
        setIsSpringBack(false);
        stretchDirectionRef.current = 0;
    }, [children, set]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            overflowX: 'hidden',
            overflowY: 'visible',
            minHeight: '900px',
        }}>
            <animated.div
                ref={containerRef}
                {...bind()}
                style={{
                    translateY: '50px',
                    touchAction: 'pan-y',
                    width: width,
                    height: '58.5vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: '15px',
                    marginTop: brand ? '-30px' : '60px',
                    transformOrigin: isSpringBack
                        ? (stretchDirectionRef.current === -1 ? 'left center' : 'right center')
                        : (stretchDirection === -1 ? 'left center' : 'right center'),
                    transform: isSpringBack
                        ? springWidth.to(val => `scaleX(${val})`)
                        : (stretchDirection !== 0
                            ? `scaleX(${1 + (stretchAmount / 300)}) translateX(${stretchDirection * stretchAmount / 10}px)`
                            : 'scaleX(1) translateX(0px)'),
                }}
            >
                {children}
            </animated.div>
        </div>
    );
};

export default StretchableHorizontalSlider;